import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  createStyles,
  Input,
  makeStyles,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
  Typography,
  useTheme
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import clsx from 'clsx';
import {
  ArrowTooltip,
  DenseIcon,
  DenseMenu,
  EnhancedIconButton
} from '.';
import { useTranslation } from 'react-i18next';
import isHtml from 'is-html';
import {
  EnhancedTableBodyProps,
  EnhancedTableCellProps,
  EnhancedTableCheckboxHeadProps,
  EnhancedTableHeadProps,
  EnhancedTableProps
} from '../types/enhanced-table';
import { toLocaleString } from '../utilities/dates';
import { useContextMenu } from '../hooks';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      fontWeight: "bold",
      userSelect: "none",
      whiteSpace: "nowrap"
    },
    densePadding: {
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1.0)}px !important`,
    },
    denseFooter: {
      height: theme.spacing(4),
      minHeight: "0px",
      overflow: "hidden"
    },
    hiddenSort: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    noSelect: {
      userSelect: "none"
    },
    rowDisabled: {
      "& th": {
        opacity: 0.3
      },
      "& td": {
        opacity: 0.3
      }
    },
    rowIcon: {
      fontSize: "1em"
    },
    buttonCell: {
      width: "1%",
      whiteSpace: "nowrap",
      padding: "0px !important"
    },
    buttonHeader: {
      padding: "0px !important"
    },
    tableRow: {
      "& td:last-child": {
        paddingRight: `${theme.spacing(1.5)}px !important`
      },
      "& td:first-child": {
        paddingLeft: `${theme.spacing(1.5)}px !important`
      },
      "& th:last-child": {
        paddingRight: `${theme.spacing(1.5)}px !important`
      },
      "& th:first-child": {
        paddingLeft: `${theme.spacing(1.5)}px !important`
      }
    }
  }),
);

/** Column header with label and checkbox */
export const EnhancedTableCheckboxHead = (props: EnhancedTableCheckboxHeadProps) => {
  const {label, values, onChange} = props;

  const numVisible = values.filter(x => x === true).length;
  const checked = numVisible > 0;
  const indeterminate = checked && (values.length !== numVisible);

  return (
    <React.Fragment>
      <Checkbox
        size="small"
        checked={checked}
        indeterminate={indeterminate}
        onClick={(event) => {
          // Stop click event from triggering sort function
          event.stopPropagation();
        }}
        onChange={(event) => {
          onChange(indeterminate || event.target.checked);
        }}
      />

      {label}
    </React.Fragment>
  );
};

const EnhancedTableHead = (props: EnhancedTableHeadProps) => {
  const {sortDir, sortBy, onRequestSort, columns, actions, dense} = props;

  const classes = useStyles();

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const hasActions = actions.length > 0;

  return (
    <TableHead>
      <TableRow className={classes.tableRow}>
        {columns.map(column => {

          const {id, label, center, sortable=true} = column;

          return (
            <TableCell
              key={id}
              align={center ? 'center' : "left"}
              sortDirection={(sortBy === id) ? sortDir : false}
              className={clsx(classes.header, {
                [classes.densePadding]: dense
              })}
            >
              {/* Non-sortable column header */}
              {!sortable && <TableSortLabel
                active={false}
                hideSortIcon={true}
                style={{
                  cursor: "default"
                }}
              >
                {label}
              </TableSortLabel>}

              {/* Sortable column header */}
              {sortable && <TableSortLabel
                active={sortBy === id}
                hideSortIcon={true}
                direction={sortBy === id ? sortDir : 'asc'}
                onClick={createSortHandler(id)}
              >
                {label}
                {sortBy === id ? (
                  <span className={classes.hiddenSort}>
                    {sortDir === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>}
            </TableCell>
          );
        })}

        {/** Custom actions */}
        {hasActions && <TableCell className={classes.buttonHeader}/>}

      </TableRow>
    </TableHead>
  );
};

const EnhancedTableBody = (props: EnhancedTableBodyProps) => {
  const {rows, actions, columns, onClick, dense,
    setSelectedRow, handleContextMenu} = props;

  const classes = useStyles();
  const hasActions = actions.length > 0;

  return (
    <TableBody style={{height: "100%"}}>
      {rows.map(row =>
        <TableRow
          hover
          tabIndex={-1}
          key={row.id}
          className={clsx(classes.noSelect, classes.tableRow, {
            [classes.rowDisabled]: row.disabled,
          })}
        >
          {columns.map((column, index) =>
            <EnhancedTableCell
              key={`CustomTableCell_${row.id}_${column.id}`}
              row={row}
              column={column}
              index={index}
              dense={dense}
              onClick={onClick}
            />
          )}

          {/** Custom actions */}
          {hasActions && <TableCell>
            <EnhancedIconButton
              title={"Actions"}
              disabled={row.disabled}
              onClick={(event) => {
                setSelectedRow(row);
                handleContextMenu(event);
              }}
            >
              <MoreHorizIcon fontSize="small"/>
            </EnhancedIconButton>
          </TableCell>}

        </TableRow>
      )}
    </TableBody>
  );
};

const EnhancedTableCell = (props: EnhancedTableCellProps) => {
  const {row, column, dense, index, onClick} = props;

  const theme = useTheme();
  const {i18n} = useTranslation();

  const key = `${row.id}_${column.id}`;
  const text = row[column.id] as any;
  const isString = typeof(text) === "string";
  const condition = (column.numeric || text.length <= 100);

  const cellContent = (column, content, condition) => {
    if (column.date) {
      const dateString = toLocaleString(content, i18n.language);
      return dateString;
    }

    const renderAsHTML = isString
      ? isHtml(content)
      : false;

    if (renderAsHTML) {
      if (column.tooltip) {
        return (
          <ArrowTooltip title={column.tooltip}>
            <div dangerouslySetInnerHTML={{__html: content}}/>
          </ArrowTooltip>
        );
      } else {
        return (
          <div dangerouslySetInnerHTML={{__html: content}}/>
        );
      }
    }

    if (isString) {
      content = condition ? content : `${content.slice(0,100)}...`;
    }

    if (column.tooltip) {
      return (
        <ArrowTooltip title={column.tooltip}>
          <div style={{
            width: "fit-content"
          }}>
            {content}
          </div>
        </ArrowTooltip>
      );
    }

    return content;
  };

  const title = isString ? text : "";
  const content = cellContent(column, text, condition);

  return (
    <TableCell
      key={key}
      align={"left"}
      onClick={() => {
        if (row.disabled || !onClick) return;
        onClick(row, index);
      }}
      style={{
        ...(column.wordBreak && {
          wordBreak: "break-all"
        }),
        ...(column.forceOpacity && {
          opacity: 1.0
        }),
        ...(column.pointer && {
          cursor: "pointer"
        }),
        ...(column.center && {
          textAlign: "center"
        }),
        ...(dense && {
          padding: `${theme.spacing(0.5)}px ${theme.spacing(1.0)}px`
        })
      }}
      title={title}
    >
      {content}
    </TableCell>
  );
};

export const EnhancedTable = (props: EnhancedTableProps) => {
  const {state, columns, rows, actions=[], onClick,
    dense = true, maxHeight} = props;

  const classes = useStyles();
  const theme = useTheme();
  const {t} = useTranslation();

  const contextMenu = useContextMenu();

  const [search, setSearch] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);

  const onSearch = (event) => {
    const value = event.target.value;
    state.handleSearchChange(value);
    setSearch(value);
  };

  useEffect(() => {
    // Restore previously saved search terms
    //setSearch(state.query);

    // Reset the search string
    setSearch("");
    state.handleSearchChange("");
  }, []);

  useEffect(() => {
    if (state.pageNumber <= maxPageNumber) return;
    state.handlePageNumberChange(null, maxPageNumber);
  }, [state.pageNumber, rows]);

  let tableRows = state.handleSearch(columns, [...rows]);

  // Number of rows must be calculated after
  // search and before pagination
  const numberOfRows = tableRows.length;

  tableRows = state.handleSort(columns, tableRows);
  tableRows = state.handlePages(tableRows);

  // Make sure our max page is valid
  const maxPageNumber = Math.max(
    0, Math.ceil(numberOfRows / state.rowsPerPage) - 1);
  const pageNumber = Math.min(state.pageNumber, maxPageNumber);

  const searchable = columns.filter(
    column => column.searchable).length > 0;

  return (
    <React.Fragment>
      <div style={{
        height: "100%",
        minHeight: "100px"
      }}>
        <Paper style={{
          display: "flex",
          height: "100%",
          flexDirection: "column"
        }}>

          {/** Search bar */}
          {searchable && <Input
            type={"search"}
            placeholder={"Search..."}
            value={search}
            onChange={onSearch}
            style={{
              margin: theme.spacing(1.5)
            }}
            startAdornment={<SearchIcon
              fontSize="small"
              style={{
                paddingRight: theme.spacing(1)
              }}
            />}
          />}

          <TableContainer style={{
            flex: 1,
            maxHeight: maxHeight ? maxHeight : "initial"
          }}>
            <Table
              stickyHeader
              style={{
                height: "100%"
              }}
              size={'small'}
            >

              <EnhancedTableHead
                sortDir={state.sortDir}
                sortBy={state.sortBy}
                onRequestSort={state.handleRequestSort}
                columns={columns}
                actions={actions}
                dense={dense}
              />

              <EnhancedTableBody
                rows={tableRows}
                columns={columns}
                actions={actions}
                onClick={onClick}
                dense={dense}
                setSelectedRow={setSelectedRow}
                handleContextMenu={contextMenu.handleOpen}
              />

            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={numberOfRows}
            rowsPerPage={state.rowsPerPage}
            labelRowsPerPage={`${t("enhanced-table.label-rows-per-page")}:`}
            labelDisplayedRows={(page) =>
              `${page.from}–${page.to} ${t("enhanced-table.displayed-rows-of")} ${page.count !== -1 ?
                page.count : `${t("enhanced-table.displayed-rows-more-than")} ${page.to}`}`
            }
            backIconButtonText={t("enhanced-table.prev-page")}
            nextIconButtonText={t("enhanced-table.next-page")}
            page={pageNumber}
            classes={{
              toolbar: dense ? classes.denseFooter : ""
            }}
            onChangePage={state.handlePageNumberChange}
            onChangeRowsPerPage={state.handleRowsPerPageChange}
          />

        </Paper>
      </div>

      {/** Context menu */}
      <DenseMenu
        open={contextMenu.open}
        onClose={contextMenu.handleClose}
        anchorPosition={contextMenu.anchorPosition}
      >
        {actions.map((action, index) =>
          <MenuItem
            key={`CustomAction_${index}`}
            onClick={() => {
              if (!selectedRow) return;
              action.onClick(selectedRow);
              contextMenu.handleClose();
            }}
          >
            <DenseIcon>
              {action.icon}
            </DenseIcon>

            <Typography variant="inherit">
              {action.title}
            </Typography>
          </MenuItem>
        )}
      </DenseMenu>

    </React.Fragment>
  );
};
